import React, { useCallback, useMemo, useState } from 'react'
import PdfPreviewPortal from 'pages/ApplicationDetailsPage/components/Checklist/components/PdfPreviewPortal/PdfPreviewPortal'
import SendMessageModal from 'pages/Students/components/SendMessageModal/SendMessageModal'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import { downloadStudentLorFile } from 'api/lorRequests'
import { fmtDateTime } from 'pages/ApplicationDetailsPage/utils/applicationDetailsFormat'
import { ReactComponent as BragsheetIcon } from 'assets/icons/contract.svg'
import s from '../../styles/BragsheetSection.module.scss'

const BRAGSHEET_NUDGE_MESSAGE = `Hi {studentName},

Please complete your Brag Sheet in Study Abroad → LoR Prep-Work so I can write a strong Letter of Recommendation for you.

Thank you!`

const BragsheetSection = ({
	studentId,
	studentName,
	rows = [],
	onReload,
}) => {
	const [reminderOpen, setReminderOpen] = useState(false)
	const [assignOpen, setAssignOpen] = useState(false)
	const [assignKey, setAssignKey] = useState(0)
	const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', title: '' })

	const selectedStudents = useMemo(
		() =>
			studentId
				? [{ id: studentId, name: studentName || 'Student' }]
				: [],
		[studentId, studentName],
	)

	const openAssign = useCallback(() => {
		setAssignKey(value => value + 1)
		setAssignOpen(true)
	}, [])

	const handleView = useCallback(
		async item => {
			if (!studentId || !item?.fileId) return
			try {
				const blob = await downloadStudentLorFile(studentId, item.fileId)
				const url = window.URL.createObjectURL(blob)
				setPdfPreview({ open: true, url, title: 'Brag Sheet' })
			} catch {
				// noop
			}
		},
		[studentId],
	)

	const closePreview = useCallback(() => {
		setPdfPreview(current => {
			if (current.url?.startsWith('blob:')) {
				try {
					window.URL.revokeObjectURL(current.url)
				} catch {}
			}
			return { open: false, url: '', title: '' }
		})
	}, [])

	const reminderMessage = useMemo(
		() =>
			BRAGSHEET_NUDGE_MESSAGE.replace(
				'{studentName}',
				studentName || 'there',
			),
		[studentName],
	)

	const completedRow = rows.find(row => row.counselorStatus === 'success')
	const assignedRow = rows.find(row => row.counselorStatus === 'assigned')
	const state = completedRow
		? 'completed'
		: assignedRow
			? 'assigned'
			: 'missing'
	const statusCopy = {
		missing: {
			label: 'Not assigned',
			title: 'Brag Sheet needed',
			description:
				'Assign the Brag Sheet so the student can share context for a stronger recommendation.',
			action: 'Assign Brag Sheet',
		},
		assigned: {
			label: 'In progress',
			title: 'Waiting on the student',
			description:
				'The Brag Sheet has been assigned. Send a reminder if it is holding up the letter.',
			action: 'Remind student',
		},
		completed: {
			label: 'Completed',
			title: 'Brag Sheet is ready',
			description: completedRow?.lastUpdated
				? `Completed ${fmtDateTime(completedRow.lastUpdated)}`
				: 'The student context is ready to use.',
			action: 'Reassign',
		},
	}[state]

	const handlePrimaryAction = useCallback(() => {
		if (state === 'assigned') {
			setReminderOpen(true)
			return
		}
		openAssign()
	}, [openAssign, state])

	return (
		<>
			<section className={[s.wrap, s[state]].join(' ')}>
				<div className={s.statusGraphic}>
					<span className={s.icon}>
						<BragsheetIcon />
					</span>
					<span className={s.progressDot} aria-hidden="true" />
				</div>
				<div className={s.content}>
					<div className={s.eyebrow}>
						<span>Brag Sheet</span>
						<span className={s.statusPill}>{statusCopy.label}</span>
					</div>
					<h3 className={s.title}>{statusCopy.title}</h3>
					<p className={s.description}>{statusCopy.description}</p>
				</div>
				<div className={s.actions}>
					{completedRow?.fileId ? (
						<button
							type="button"
							className={s.secondaryButton}
							onClick={() => handleView(completedRow)}
						>
							View latest
						</button>
					) : null}
					<button
						type="button"
						className={s.primaryButton}
						onClick={handlePrimaryAction}
					>
						{statusCopy.action}
					</button>
				</div>
			</section>

			<SendMessageModal
				isOpen={reminderOpen}
				onClose={() => setReminderOpen(false)}
				selectedStudents={selectedStudents}
				initialMessage={reminderMessage}
			/>

			<PdfPreviewPortal
				open={pdfPreview.open}
				url={pdfPreview.url}
				title={pdfPreview.title}
				onClose={closePreview}
			/>

			<AssignActivityModal
				key={assignKey}
				isOpen={assignOpen}
				onClose={() => setAssignOpen(false)}
				selectedStudents={selectedStudents}
				lockRecipients
				initialAssignmentType="bragsheet_builder"
				initialTaskTitle="Complete Brag Sheet for LoR"
				onSuccess={() => {
					setAssignOpen(false)
					onReload?.()
				}}
			/>
		</>
	)
}

export default BragsheetSection
